<template>
  <div class="select-chart-container" :id="id">
    <v-row>
      <v-col :cols="averageScores ? 6 : 8" class="mb-2 ml-2 mt-2">
        <v-select
            :items="availableItems"
            density="compact"
            variant="outlined"
            v-model="selectedItem"
            :label="selectPrompt"
            @update:model-value="updateData"
        >
          <template v-slot:selection>
            {{ StringManipulation.methods.clearAndCapitalize(selectedItem) }}
          </template>

          <template v-slot:item="{ item, props }" >
            <v-list-item  v-text="StringManipulation.methods.clearAndCapitalize(item.title)" v-bind="props"/>
          </template>

        </v-select>
      </v-col>
      <v-col v-if="showFinalSII" cols="4" class="mb-2 m-2">
        <FormCheckbox
          :enabledSiiScores="enableSiiScores"
          :isSpiOrSii="isSpiOrSii"
          @update-data="updateCheckboxData"
        ></FormCheckbox>

      </v-col>
      <v-spacer v-else />
      <v-col cols='auto' class="mt-2">
        <DownloadChartButton :id="id" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <h5 v-if="selectedItem" style="text-align: center; font-size: 22px">
          {{ StringManipulation.methods.clearAndCapitalize(selectedItem) }}
        </h5>
        <h5 v-else>Please select an item from the list</h5>
      </v-col>
      <v-col cols="12">
        <BarChart
            v-if="itemChartData && chartType === 'vertical'"
            :chartData="itemChartData"
            :options="chart.options"
            :key="selectedItem"
            :chart-id="isSpiOrSii"
            class="select-chart-plot"
        ></BarChart>

        <HorizontalBarChart
            v-if="itemChartData && chartType === 'horizontal'"
            :chartData="itemChartData"
            :options="chart.options"
            class="select-chart-plot"
        >
        </HorizontalBarChart>
      </v-col>
      <v-col cols="12">
        <AverageScore v-if="averageScores"
            colorTable="activeLevels"
            :showFinalSII="showFinalSII"
            :activeLevels="activeLevels"
            :colors="activeColors"
            :averageScores="averageScores"
            :isSpiOrSii="isSpiOrSii"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import BarChart from "../base/BarChart.vue";
import HorizontalBarChart from "../base/HorizontalBarChart.vue";
import AverageScore from "../components/AverageScore.vue";
import FormSelect from "../components/FormSelect.vue";
import FormCheckbox from "../components/FormCheckbox.vue";
import Exporter from "vue-chartjs-exporter";
import { computed, onMounted, ref } from "vue";
import Utils from "../../Mixins/Utils";
import StringManipulation from "../../Mixins/StringManipulation";
import {hu} from "vuetify/locale";
import {Chart} from "chart.js";
import DownloadChartButton from "@/Charts/components/DownloadChartButton.vue";

const props = defineProps({
  selectPrompt: {
    type: String,
    required: false,
    default: "Please select an item from the list",
  },
  availableItems: {
    type: Array,
    required: true,
  },
  itemsData: {
    type: Object,
    required: true,
  },
  averageScores: {
    type: Object,
    required: false,
  },
  showFinalSII: {
    type: Boolean,
    required: false,
    default: false,
  },
  isSpiOrSii: {
    type: String,
    required: false,
    default: "sii",
  },
  chartType: {
    type: String,
    required: false,
    default: "vertical",
  },
  id: {
    type: String,
    required: false,
  },
});

const enableSiiScores = ref(false);
const itemChartData = ref(null);
const selectedItem = ref('');
const activeLevels = ref([]);
const activeColors = ref([])
const colors = {
  'building': "rgb(189,33,34)",
  'city': "rgb(216,163,23)",
  'island': "rgb(111,39,179)",
  'peb': "rgb(27,128,197)",
  'ped': "rgb(21,129,14)"
};

const chart = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    options: {
      plugins: {
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15,
            }
          }
        },
        'bar-x-axis1': {
          display: false,
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15,
            }
          }
        },
        y: {
          max: 5,
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15,
            }
          }
        }
      }
    },
  };
});

const averageType = computed(() => {
  if (this.isSpiOrSii === "sii") return "SII";
  else return "SPI";
});

onMounted(() => {
  // Chart.defaults.global.defaultFontColor = 'white'
  updateData(Object.keys(props.itemsData)[0]);
});

function updateData(item) {
  const itemData = props.itemsData[item]["data"];
  // this.chart.options.annotation.annotations = []
  activeLevels.value = [];
  activeColors.value = [];
  selectedItem.value = item;
  const averageDataset = {
    label: "Average Score",
    data: [],
    backgroundColor: [],
    borderWidth: 1,
    barThickness: 15,
  };

  for (let i = 0; i < itemData.labels.length; i++) {
    // this.chart.options.annotation.annotations.push(this.annotationObject(itemData.labels[i],
    // this.averageScores[itemData.labels[i]], colors[i]))
    const label = itemData.labels[i]
    activeLevels.value.push(label);
    activeColors.value.push(colors[label.toLowerCase()])
    if (props.showFinalSII && enableSiiScores.value) {
      averageDataset.data.push(
        props.averageScores[itemData.labels[i].toLowerCase()]
      );
      averageDataset.backgroundColor.push(colors[label.toLowerCase()])
    }
  }
  itemChartData.value = Utils.methods.deepCopy(props.itemsData[item]["data"]);

  if (enableSiiScores.value) {
    itemChartData.value.datasets.unshift(averageDataset);
  }
}

function updateCheckboxData(item) {
  enableSiiScores.value = item;
  updateData(selectedItem.value);
}

function printChart(id) {
  let graphical = document.getElementById(id);
  const exp = new Exporter([graphical]);
  exp.export_pdf().then((pdf) => pdf.save("charts.pdf"));
}
</script>

<style scoped></style>
